// src/pages/Campaigns/SurveyGenie/surveyEnums.js

/**
 * SurveyGenie - Survey Management System Enums and Constants
 */

// Survey Status
export const SurveyStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  CLOSED: 'CLOSED',
  ARCHIVED: 'ARCHIVED'
};

export const SurveyStatusLabels = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived'
};

export const SurveyStatusColors = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  CLOSED: 'error',
  ARCHIVED: 'default'
};

// Question Types
export const QuestionType = {
  SINGLE_CHOICE: 'SINGLE_CHOICE',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  TEXT_SHORT: 'TEXT_SHORT',
  TEXT_LONG: 'TEXT_LONG',
  RATING_SCALE: 'RATING_SCALE',
  LIKERT_SCALE: 'LIKERT_SCALE',
  DROPDOWN: 'DROPDOWN',
  RANKING: 'RANKING',
  MATRIX: 'MATRIX',
  DATE: 'DATE',
  DATE_TIME: 'DATE_TIME',
  FILE_UPLOAD: 'FILE_UPLOAD',
  NET_PROMOTER_SCORE: 'NET_PROMOTER_SCORE',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  NUMBER: 'NUMBER',
  SLIDER: 'SLIDER',
  YES_NO: 'YES_NO',
  SECTION_BREAK: 'SECTION_BREAK'
};

// ALIAS for components using UPPER_CASE imports
export const QUESTION_TYPE = QuestionType;

export const QuestionTypeLabels = {
  SINGLE_CHOICE: 'Single Choice',
  MULTIPLE_CHOICE: 'Multiple Choice',
  TEXT_SHORT: 'Short Text',
  TEXT_LONG: 'Long Text',
  RATING_SCALE: 'Rating Scale',
  LIKERT_SCALE: 'Likert Scale',
  DROPDOWN: 'Dropdown',
  RANKING: 'Ranking',
  MATRIX: 'Matrix',
  DATE: 'Date',
  DATE_TIME: 'Date & Time',
  FILE_UPLOAD: 'File Upload',
  NET_PROMOTER_SCORE: 'NPS Score',
  EMAIL: 'Email',
  PHONE: 'Phone',
  NUMBER: 'Number',
  SLIDER: 'Slider',
  YES_NO: 'Yes/No',
  SECTION_BREAK: 'Section Break'
};

// ALIAS for components using UPPER_CASE imports
export const QUESTION_TYPE_LABELS = QuestionTypeLabels;

export const QuestionTypeIcons = {
  SINGLE_CHOICE: 'RadioButtonChecked',
  MULTIPLE_CHOICE: 'CheckBox',
  TEXT_SHORT: 'ShortText',
  TEXT_LONG: 'Notes',
  RATING_SCALE: 'Star',
  LIKERT_SCALE: 'LinearScale',
  DROPDOWN: 'ArrowDropDownCircle',
  RANKING: 'FormatListNumbered',
  MATRIX: 'GridOn',
  DATE: 'CalendarToday',
  DATE_TIME: 'Schedule',
  FILE_UPLOAD: 'CloudUpload',
  NET_PROMOTER_SCORE: 'Speed',
  EMAIL: 'Email',
  PHONE: 'Phone',
  NUMBER: 'Pin',
  SLIDER: 'Tune',
  YES_NO: 'ToggleOn',
  SECTION_BREAK: 'ViewStream'
};

// Question types that have options
export const QuestionTypesWithOptions = [
  QuestionType.SINGLE_CHOICE,
  QuestionType.MULTIPLE_CHOICE,
  QuestionType.DROPDOWN,
  QuestionType.RANKING,
  QuestionType.MATRIX
];

// Question types that are numeric
export const NumericQuestionTypes = [
  QuestionType.RATING_SCALE,
  QuestionType.NET_PROMOTER_SCORE,
  QuestionType.NUMBER,
  QuestionType.SLIDER
];

// Distribution Channels
export const DistributionChannel = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  LINK: 'LINK',
  EMBED: 'EMBED',
  CHATBOT: 'CHATBOT',
  QR_CODE: 'QR_CODE'
};

// ALIAS for components using UPPER_CASE imports
export const DISTRIBUTION_CHANNEL = DistributionChannel;

export const DistributionChannelLabels = {
  EMAIL: 'Email',
  SMS: 'SMS',
  LINK: 'Public Link',
  EMBED: 'Website Embed',
  CHATBOT: 'Chatbot',
  QR_CODE: 'QR Code'
};

// ALIAS for components using UPPER_CASE imports
export const DISTRIBUTION_CHANNEL_LABELS = DistributionChannelLabels;

// Response Status
export const ResponseStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  ABANDONED: 'ABANDONED',
  DISQUALIFIED: 'DISQUALIFIED'
};

export const ResponseStatusLabels = {
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  ABANDONED: 'Abandoned',
  DISQUALIFIED: 'Disqualified'
};

export const ResponseStatusColors = {
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  ABANDONED: 'warning',
  DISQUALIFIED: 'error'
};

// Survey Template Categories
export const SurveyTemplateCategory = {
  CUSTOMER_SATISFACTION: 'CUSTOMER_SATISFACTION',
  EMPLOYEE_ENGAGEMENT: 'EMPLOYEE_ENGAGEMENT',
  MARKET_RESEARCH: 'MARKET_RESEARCH',
  EVENT_FEEDBACK: 'EVENT_FEEDBACK',
  PRODUCT_FEEDBACK: 'PRODUCT_FEEDBACK',
  ACADEMIC_RESEARCH: 'ACADEMIC_RESEARCH',
  HEALTHCARE: 'HEALTHCARE',
  CUSTOM: 'CUSTOM'
};

export const SurveyTemplateCategoryLabels = {
  CUSTOMER_SATISFACTION: 'Customer Satisfaction',
  EMPLOYEE_ENGAGEMENT: 'Employee Engagement',
  MARKET_RESEARCH: 'Market Research',
  EVENT_FEEDBACK: 'Event Feedback',
  PRODUCT_FEEDBACK: 'Product Feedback',
  ACADEMIC_RESEARCH: 'Academic Research',
  HEALTHCARE: 'Healthcare',
  CUSTOM: 'Custom'
};

// Question Bank Categories
export const QuestionBankCategory = {
  DEMOGRAPHICS: 'DEMOGRAPHICS',
  SATISFACTION: 'SATISFACTION',
  NPS: 'NPS',
  FEEDBACK: 'FEEDBACK',
  PRODUCT: 'PRODUCT',
  SERVICE: 'SERVICE',
  EMPLOYEE: 'EMPLOYEE',
  MARKETING: 'MARKETING',
  CUSTOM: 'CUSTOM'
};

// ALIAS for components using UPPER_CASE imports
export const QUESTION_BANK_CATEGORY = QuestionBankCategory;

export const QuestionBankCategoryLabels = {
  DEMOGRAPHICS: 'Demographics',
  SATISFACTION: 'Satisfaction',
  NPS: 'Net Promoter Score',
  FEEDBACK: 'General Feedback',
  PRODUCT: 'Product',
  SERVICE: 'Service',
  EMPLOYEE: 'Employee',
  MARKETING: 'Marketing',
  CUSTOM: 'Custom'
};

// ALIAS for components using UPPER_CASE imports
export const QUESTION_BANK_CATEGORY_LABELS = QuestionBankCategoryLabels;

// Logic Operators
export const LogicOperator = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  GREATER_THAN: 'greater_than',
  LESS_THAN: 'less_than',
  IS_ANSWERED: 'is_answered',
  IS_NOT_ANSWERED: 'is_not_answered'
};

export const LogicOperatorLabels = {
  equals: 'Equals',
  not_equals: 'Does not equal',
  contains: 'Contains',
  not_contains: 'Does not contain',
  greater_than: 'Greater than',
  less_than: 'Less than',
  is_answered: 'Is answered',
  is_not_answered: 'Is not answered'
};

// Logic Actions
export const LogicAction = {
  SHOW: 'show',
  HIDE: 'hide',
  SKIP_TO: 'skip_to',
  END_SURVEY: 'end_survey'
};

export const LogicActionLabels = {
  show: 'Show question',
  hide: 'Hide question',
  skip_to: 'Skip to question',
  end_survey: 'End survey'
};

// A/B Test Status
export const ABTestStatus = {
  DRAFT: 'DRAFT',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

export const ABTestStatusLabels = {
  DRAFT: 'Draft',
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled'
};

// Panelist Status
export const PanelistStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  BLOCKED: 'BLOCKED',
  PENDING: 'PENDING'
};

// ALIAS for components using UPPER_CASE imports
export const PANELIST_STATUS = PanelistStatus;

export const PanelistStatusLabels = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  BLOCKED: 'Blocked',
  PENDING: 'Pending'
};

// ALIAS for components using UPPER_CASE imports
export const PANELIST_STATUS_LABELS = PanelistStatusLabels;

// Sentiment Types
export const SentimentType = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  NEUTRAL: 'NEUTRAL',
  MIXED: 'MIXED'
};

export const SentimentTypeLabels = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  NEUTRAL: 'Neutral',
  MIXED: 'Mixed'
};

export const SentimentColors = {
  POSITIVE: '#4caf50',
  NEGATIVE: '#f44336',
  NEUTRAL: '#9e9e9e',
  MIXED: '#ff9800'
};

// Likert Scale Options
export const LikertScaleOptions = {
  5: [
    'Strongly Disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly Agree'
  ],
  7: [
    'Strongly Disagree',
    'Disagree',
    'Somewhat Disagree',
    'Neutral',
    'Somewhat Agree',
    'Agree',
    'Strongly Agree'
  ]
};

// Default Question Settings by Type
export const DefaultQuestionSettings = {
  RATING_SCALE: {
    min_value: 1,
    max_value: 5,
    min_label: 'Poor',
    max_label: 'Excellent'
  },
  LIKERT_SCALE: {
    scale_points: 5,
    scale_type: '5_point'
  },
  NET_PROMOTER_SCORE: {
    min_value: 0,
    max_value: 10,
    min_label: 'Not at all likely',
    max_label: 'Extremely likely'
  },
  SLIDER: {
    min_value: 0,
    max_value: 100,
    step: 1
  },
  TEXT_SHORT: {
    max_length: 250,
    placeholder: ''
  },
  TEXT_LONG: {
    max_length: 2000,
    placeholder: ''
  },
  NUMBER: {
    min_value: null,
    max_value: null,
    decimal_places: 0
  }
};

// Export helper functions
export const getStatusColor = (status) => {
  return SurveyStatusColors[status] || 'default';
};

export const getQuestionTypeLabel = (type) => {
  return QuestionTypeLabels[type] || type;
};

export const hasOptions = (questionType) => {
  return QuestionTypesWithOptions.includes(questionType);
};

export const isNumericQuestion = (questionType) => {
  return NumericQuestionTypes.includes(questionType);
};

// FIXED: Added this function which was missing but used in SurveyBuilder
export const getDefaultSettings = (type) => {
  return DefaultQuestionSettings[type] || {};
};